import { useEffect, useMemo, useState } from 'react';
import { ResourceGrid } from '../../components/ResourceGrid';
import { type TagFilter, emptyTagFilter } from '../../components/TagFilterBuilder';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Tabs, type TabDef } from '../../components/Tabs';
import { CollectionPreProcessingTab } from './CollectionPreProcessingTab';
import { CollectionPostProcessingTab } from './CollectionPostProcessingTab';
import { getRouteParams, openResourceViewer, setCurrentCollection } from '../../services/angular-bridge';
import { createBrowseDatasource } from '../../services/resourceDatasource';
import { useProject } from '../../hooks/useProject';
import { useCollection } from '../../hooks/useCollections';
import { useResource } from '../../hooks/useResource';
import { useProjectPageMount } from '../../hooks/useProjectPageMount';
import { NOT_FOUND_META, isNotFound, useNotFoundRedirect } from '../../hooks/useNotFoundRedirect';
import { useInitialSearch } from '../../hooks/useInitialSearch';
import { hasPermission } from '../../types/project';
import { NewCollectionModal } from '../ProjectDetailPage/NewCollectionModal';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';

const ADD_DATA_HELP_URL =
  'https://folio-accessid.zendesk.com/hc/en-us/articles/213127166-Adding-Data-To-Your-Project';

/**
 * CollectionPage (/project/:id/collection/:cid). Header + breadcrumb, then the collection.html
 * tabset ported to React: Pre-Processing Overview / Post-Processing Overview / Browse
 * (the resource grid). Defaults to the Pre-Processing overview, as Angular did.
 */
export function CollectionPage() {
  // resourceId: /resource/:resourceId deep link. q: `?q=` search to run on load.
  const { projectId, collectionId, resourceId, q } = getRouteParams();
  const projectQuery = useProject(projectId, NOT_FOUND_META);
  const collectionQuery = useCollection(projectId, collectionId, NOT_FOUND_META);
  const resourceQuery = useResource(projectId, collectionId, resourceId ?? '', NOT_FOUND_META);
  const { data: project } = projectQuery;
  const { data: collection } = collectionQuery;
  const { data: resource } = resourceQuery;
  const canManageCollections = !!project && hasPermission(project, 'MANAGE_COLLECTIONS');
  // Before the collection effect below: the mount hook clears the collection scope.
  useProjectPageMount(project, 'projects');
  const hasIdentifiers = !!(collection?.ritm || collection?.evidence || collection?.release);

  // Fall back to the deepest level that exists. A hibernated project's collections can't be
  // fetched, which isn't "not found".
  const hibernated = project?.status === 'hibernate';
  const collectionMissing = !!project && !hibernated && isNotFound(collectionQuery);
  useNotFoundRedirect(projectQuery);
  useNotFoundRedirect(collectionQuery, {
    to: `/project/${projectId}`,
    toast: 'Collection not found',
    enabled: !!project && !hibernated,
  });
  useNotFoundRedirect(resourceQuery, {
    to: `/project/${projectId}/collection/${collectionId}`,
    toast: 'Resource not found',
    enabled: !!collection,
  });

  // Make this the Angular "current collection" so Collection-scoped searches (chart
  // clicks in the Post-Processing tab) resolve to it in the search bar.
  useEffect(() => {
    if (collection) setCurrentCollection(collection);
  }, [collection]);

  // After the effect above, so the search is scoped to this collection. A resource link
  // searches for the resource only once it's known to exist.
  const initialQuery = resourceId ? (resource ? resourceId : undefined) : typeof q === 'string' ? q : undefined;
  useInitialSearch(initialQuery, 0, !!project && !!collection);

  useEffect(() => {
    if (resource?.id) openResourceViewer({ projectId, collectionId, resourceId: resource.id });
  }, [resource?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tag filter for this collection, built via the grid toolbar's filter panel. null = no
  // filter (browse the whole collection).
  const [filter, setFilter] = useState<TagFilter | null>(null);
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);

  const datasource = useMemo(
    () =>
      createBrowseDatasource({
        projectId,
        collectionIds: collectionId,
        tagFilter: filter ? JSON.stringify(filter) : '',
      }),
    [projectId, collectionId, filter]
  );

  const bulkContext = useMemo(
    () => ({
      projectId,
      projectName: project?.name,
      collectionIds: [collectionId],
      // Never null: the backend's FilterRequest is @NotNull. An empty filter = the whole
      // collection (Angular's GetCleanFilter, which is what its report/tag/batch calls sent).
      filter: filter ?? emptyTagFilter(projectId),
      canManageTags: !!project && hasPermission(project, 'MANAGE_TAGS'),
      canCreateBatches: !!project && hasPermission(project, 'MANAGE_BATCHES'),
      canManageReports: !!project && hasPermission(project, 'MANAGE_REPORTS'),
    }),
    [projectId, collectionId, project, filter]
  );

  const tabs: TabDef[] = [
    {
      id: 'pre',
      label: 'Pre-Processing Overview',
      testId: 'tab-pre-processing',
      content: (
        <CollectionPreProcessingTab
          projectId={projectId}
          collectionId={collectionId}
          collection={collection}
          project={project}
        />
      ),
    },
    {
      id: 'post',
      label: 'Post-Processing Overview',
      testId: 'tab-post-processing',
      // Post-processing metrics only exist once the collection is Processed (Angular disabled it otherwise).
      disabled: collection?.status !== 'Processed',
      content: <CollectionPostProcessingTab projectId={projectId} collectionId={collectionId} />,
    },
    {
      id: 'browse',
      label: 'Browse',
      disabled: collection?.status === 'Paused',
      // Scoped to a single collection, so the per-row collection name is redundant.
      content: (
        <ResourceGrid
          datasource={datasource}
          showCollection={false}
          bulkContext={bulkContext}
          tagFilter={{ projectId, filter, onApply: setFilter }}
          testId="collection-home-result-grid"
        />
      ),
    },
  ];

  // Hold the page until both lookups settle, so a missing project/collection redirects
  // without first flashing the page shell (and firing its tabs' requests).
  const settled = (query: { data?: unknown; isError: boolean }) => !!query.data || query.isError;
  if (!settled(projectQuery) || !settled(collectionQuery) || isNotFound(projectQuery) || collectionMissing) {
    return <LoadingPlaceholder label="Loading collection…" />;
  }

  return (
    <div className="container-fluid">
      {/* Matches collection.html: title + identifiers + custodian all inline (pull-left)
          in the left column, above the header divider; actions on the right. */}
      <div className="page-header" data-testid="collection-home-header-panel">
        <div className="row">
          <div className="col-sm-7">
            <h1 className="pull-left" data-testid="collection-home-title-heading"><i className="far fa-hdd" aria-hidden="true" /> {collection?.name ?? 'Collection'}</h1>
            {hasIdentifiers && (
              <dl className="dl-horizontal pull-left" style={{ marginBottom: 0, fontSize: '67%', color: '#999' }}>
                <dt style={{ width: 60 }}>Identifier 1</dt><dd style={{ marginLeft: 80 }}>{collection?.ritm}</dd>
                <dt style={{ width: 60 }}>Identifier 2</dt><dd style={{ marginLeft: 80 }}>{collection?.evidence}</dd>
                <dt style={{ width: 60 }}>Identifier 3</dt><dd style={{ marginLeft: 80 }}>{collection?.release}</dd>
              </dl>
            )}
            {collection?.custodian?.name && (
              <h4 className="pull-left" style={{ marginLeft: 20, color: '#999' }} data-testid="collection-home-custodian-heading">
                <i className="fas fa-user" aria-hidden="true" /> {collection.custodian.name}
              </h4>
            )}
          </div>
          {canManageCollections && (
            <div className="col-sm-5 text-right">
              <a href={ADD_DATA_HELP_URL} target="_blank" rel="noreferrer" title="Get Help" style={{ marginRight: 8 }} data-testid="collection-home-help-link">
                <i className="far fa-question-circle fa-lg" aria-hidden="true" />
              </a>
              <button type="button" className="btn btn-primary" onClick={() => setAddCollectionOpen(true)} data-testid="collection-home-add-data-btn">
                Add more data to this project <i className="fas fa-plus-circle" style={{ color: '#fff' }} />
              </button>
            </div>
          )}
        </div>
      </div>
      <Breadcrumb
        items={[
          { label: 'Projects', href: '/project' },
          { label: project?.name ?? 'Project', href: `/project/${projectId}/collections` },
          { label: collection?.name ?? 'Collection' },
        ]}
        testId="collection-home-breadcrumb-list"
      />

      <Tabs tabs={tabs} defaultTab="pre" testId="collection-home-tabs" />
      {addCollectionOpen && project && (
        <NewCollectionModal project={project} onClose={() => setAddCollectionOpen(false)} />
      )}
    </div>
  );
}
